import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  Type,
} from '@angular/core';
import {
  EProfileTypeDTO,
  IDoctorToInvite,
  IHealthCenterAdminToInvite,
  IPatientToInvite,
  IPatientToSignup,
  IProfileTagDTO,
  IUsersInvitationResult,
  IUsersSignupResult,
  TUserToInvite,
  TUserToInviteDTO,
} from '@project/view-models';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { NotificationsService } from '@lib/notifications';
import { TranslateService } from '@project/translate';
import { IModalComponent, IModalComponentRef, ModalOverlayService } from '@lib/modal';
import { UserInviteService } from '../user-invite.service';

import { DoctorInviteImportModalComponent } from '../doctors/doctor-invite-import-modal/doctor-invite-import-modal.component';
import { PatientInviteImportModalComponent } from '../patients/patient-invite-import-modal/patient-invite-import-modal.component';
import { ReceptionistInviteImportModalComponent } from '../receptionists/receptionist-invite-import-modal/receptionist-invite-import-modal.component';
import { ExportToCsv } from 'export-to-csv';
import { TGuid } from '@core/helpers';
import { HCConfigFeatures } from '../../../../../hc-config/hc-config';
import { HcFeature, HcService } from '@project/services';

@Component({
  selector: 'app-user-invite',
  templateUrl: './user-invite.component.html',
  styleUrls: ['./user-invite.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserInviteComponent implements OnDestroy, OnChanges {
  @Input() userType: EProfileTypeDTO;
  @Input() pageTitle: string;
  @Input() editableUser: TUserToInvite;
  @Input() isGroup = false;
  @Input() groupDetails: IProfileTagDTO;
  @Input() editableGroup: IProfileTagDTO;
  @Output() editedGroup = new EventEmitter<IProfileTagDTO>();
  @Input() isPending = false;
  @Input() isEditing: string;
  @Input() fatherPatientId?: TGuid;
  @Input() importDesable? = false;
  @Input() fatherTagId?: TGuid;

  @Output() invitedUsers = new EventEmitter<boolean>();
  @Output() editedUser = new EventEmitter<Record<string, unknown>>();

  public _errorMessage$ = new BehaviorSubject<TUserToInviteDTO[]>([]);
  public inviteError$ = this._errorMessage$.asObservable();

  private _usersList$ = new BehaviorSubject<TUserToInvite[]>([]);
  public usersList$ = this._usersList$.asObservable();

  private _noEmailUsersList$ = new BehaviorSubject<IPatientToSignup[]>([]);
  public noEmailUsersList$ = this._noEmailUsersList$.asObservable();

  private _dependentsList$ = new BehaviorSubject<IPatientToSignup[]>([]);
  public dependentsList$ = this._dependentsList$.asObservable();

  public hasUsersToInvite$: Observable<boolean> = this.usersList$.pipe(map((users) => !!users.length));
  public hasUsersToSignUp$: Observable<boolean> = this.noEmailUsersList$.pipe(map((users) => !!users.length));

  private _inProgress$ = new BehaviorSubject<boolean>(false);
  public inProgress$ = this._inProgress$.asObservable();

  public readonly profileTypes = EProfileTypeDTO;

  private csvImportModalRef?: IModalComponentRef<any>;
  private destroy$ = new Subject();

  private readonly importModalsByUserTypes: { [type in EProfileTypeDTO]?: Type<IModalComponent> } = {
    [EProfileTypeDTO.Doctor]: DoctorInviteImportModalComponent,
    [EProfileTypeDTO.Patient]: PatientInviteImportModalComponent,
    [EProfileTypeDTO.Receptionist]: ReceptionistInviteImportModalComponent,
  };

  public canImportFromFile = false;

  ngOnChanges(changes: SimpleChanges) {
    if (changes.userType) {
      this.canImportFromFile = !!this.importModalsByUserTypes[this.userType];
    }
  }

  constructor(
    private notificationsService: NotificationsService,
    private userInviteService: UserInviteService,
    private modalOverlayService: ModalOverlayService,
    private hcService: HcService,
  ) {}

  @HcFeature(HCConfigFeatures.PATIENT_COMPLEMENTARY_DATA_FORM_INCLUDE_ATYPICAL_CHILDREN_GUARDIAN)
  public readonly hcIncludeAtypicalChildrenGuardian: boolean;

  addUser(userData: TUserToInvite) {
    if (this.isEditMode) {
      if (this.userType === EProfileTypeDTO.Doctor) {
        this.editedUser.emit({
          ...this.editableUser,
          firstName: userData.first_name,
          lastName: userData.last_name,
          gender: userData.gender,
          specialization: (userData as IDoctorToInvite).specialization,
          phone: userData.phone,
          licenses: (userData as IDoctorToInvite).licenses,
          birthDate: new Date(userData.date_birth),
          email: userData.email,
          cpf: (userData as IDoctorToInvite).cpf,
          cns: userData.cns,
          tag: userData.tag ?? null,
        });
      } else {
        this.editedUser.emit({
          ...this.editableUser,
          firstName: userData.first_name,
          lastName: userData.last_name,
          gender: userData.gender,
          phone: userData.phone,
          birthDate: new Date(userData.date_birth),
          email: userData.email,
          external_id: userData.external_id ?? null,
          cns: userData.cns ?? null,
          accessToken: userData.access_token,
          tag: userData.tag ?? null,
          ...(this.userType === EProfileTypeDTO.HealthCenterAdmin && {
            healthCenterId: (userData as IHealthCenterAdminToInvite).health_center_id,
          }),
          ...(this.hcIncludeAtypicalChildrenGuardian && {
            isAtypicalChildGuardian: (userData as IPatientToInvite).is_atypical_child_guardian ?? null,
          }),
        });
      }

      return;
    }

    const usersToInvite = this._usersList$.value;
    if (usersToInvite.find((userToInvite) => userToInvite.email === userData.email)) {
      this.notificationsService.error({
        message: TranslateService.localize('invitations.errors.email-duplicate'),
      });
      return;
    }

    if (this.userType === EProfileTypeDTO.Patient && this.fatherPatientId) {
      (userData as IPatientToInvite).father_patient_id = this.fatherPatientId;
    }

    this._usersList$.next(this._usersList$.value.concat([userData]));
  }

  addNoEmailUser(userData: IPatientToSignup): void {
    if (!userData) {
      return;
    }

    const formattedUserData = this.formatUserData(userData);
    const updatedUserList = this.getUpdatedUserList(formattedUserData);

    this._noEmailUsersList$.next(updatedUserList);
  }

  addDependentUser(userData: IPatientToSignup): void {
    if (!userData) {
      return;
    }
    const formattedUserData = this.formatUserData(userData);
    const updatedUserList = this.getUpdatedDependentList(formattedUserData);

    this._dependentsList$.next(updatedUserList);
  }

  private formatUserData(userData: IPatientToSignup): IPatientToSignup {
    const { date_birth: dateBirth, tag, cpf, ...rest } = userData;
    return {
      ...rest,
      cpf: this.formatCpf(cpf),
      group_id: tag || null,
      date_of_birth: this.formatDate(dateBirth),
      ...this.getPatientAdditionalData(),
    };
  }

  private formatCpf(cpf?: string): string | null {
    return cpf ? cpf.replace(/\D/g, '') : null;
  }

  private formatDate(date?: Date): string | null {
    return date ? new Date(date).toLocaleDateString('pt-BR') : null;
  }

  private getPatientAdditionalData(): Partial<IPatientToInvite> {
    return this.userType === EProfileTypeDTO.Patient && this.fatherPatientId
      ? { father_patient_id: this.fatherPatientId }
      : {};
  }

  private getUpdatedUserList(user: IPatientToSignup): IPatientToSignup[] {
    return [...this._noEmailUsersList$.value, user];
  }

  private getUpdatedDependentList(user: IPatientToSignup): IPatientToSignup[] {
    return [...this._dependentsList$.value, user];
  }

  addUsers(usersData: TUserToInvite[]) {
    const usersToInvite = this._usersList$.value;
    const filteredUsersToAdd = usersData
      // Filter duplicates by email in batch
      .filter((userToAdd, index) => usersData.findIndex((user) => user.email === userToAdd.email) === index)
      // Filter duplicates by email with list to invite
      .filter((userToAdd) => !usersToInvite.find((userToInvite) => userToInvite.email === userToAdd.email));

    if (usersData.length !== filteredUsersToAdd.length) {
      this.notificationsService.error({
        message: TranslateService.localize('invitations.errors.email-duplicate-multi'),
      });
    }

    this._usersList$.next(this._usersList$.value.concat(filteredUsersToAdd));
  }

  addGroup(groupData: IProfileTagDTO) {
    if (this.isEditMode) {
      this.editedGroup.emit(groupData);
    } else {
      console.log('Grupo criado:', groupData);
    }
  }

  private handleOnlyErrorMessages(errorMessages: string[]): void {
    const html = this.generateHtmlTable(errorMessages);
    this.notificationsService.error({ message: html, durationMs: 5000 });
  }

  private handleInvitationFailure(result: IUsersInvitationResult): void {
    const success = result.count_success;
    const failed = result.count_failed;
    const html = this.generateErrorTableWithSummary(result.errorMessages, success, failed);
    this._errorMessage$.next(result.failedUsersFormData);
    this.notificationsService.error({ message: html, durationMs: 15000 });
  }

  private handleInvitationSuccess(): void {
    this.notificationsService.success({
      message: TranslateService.localize('success-messages.users-are-invited'),
    });
    this.invitedUsers.next(true);
  }

  private finalizeInvitationProcess(): void {
    this._usersList$.next([]);
    this._noEmailUsersList$.next([]);
    this._dependentsList$.next([]);
    this._inProgress$.next(false);
  }

  private handleSignupResult(result: IUsersSignupResult, users: IPatientToSignup[]): void {
    if (this.isOnlyErrorMessages(result)) {
      const html = this.generateHtmlTable(result.errorMessages);
      this.showErrorNotification(html, 5000);
    } else if (!result.isSuccess) {
      const html = this.generateErrorTableWithSummary(result.errorMessages, result.count_success, result.count_failed);
      this._errorMessage$.next(result.failedUsersFormData);
      this.showErrorNotification(html, 15000);
    } else {
      this.openSuccessModal(users);
      this.invitedUsers.next(true);
    }
  }

  private isOnlyErrorMessages(result: IUsersInvitationResult): boolean {
    return result.count_failed === 0 && result.count_success === 0 && result.errorMessages.length > 0;
  }

  private generateHtmlTable(messages: string[]): string {
    let html = '<table>';
    messages.forEach((msg) => {
      html += `<tr><td>${msg}</td></tr>`;
    });
    html += '</table>';
    return html;
  }

  private generateErrorTableWithSummary(messages: string[], success: number, failed: number): string {
    let html = '<table>';
    messages.forEach((msg) => {
      html += `<tr><td>${msg.replace(',', '')}</td></tr>`;
    });
    html += `<tr><td>[Total: ${failed + success}, Success: ${success}, Failed: ${failed}]</td></tr>`;
    html += '</table>';
    return html;
  }

  private showErrorNotification(message: string, durationMs: number): void {
    this.notificationsService.error({ message, durationMs });
  }

  private openSuccessModal(users: IPatientToSignup[]): void {
    this.notificationsService.success({
      message: TranslateService.localize('success-messages.users-are-signed-up'),
    });
  }

  sendSignup() {
    const users = this._noEmailUsersList$.value;
    this._inProgress$.next(true);

    this.userInviteService.signupAllUsers(users, this.userType).subscribe((result: IUsersSignupResult) => {
      this.handleSignupResult(result, users);
      this._noEmailUsersList$.next([]);
      this._inProgress$.next(false);
    });
  }

  sendDependentInvitations(): void {
    const users = this._dependentsList$.value;
    this._inProgress$.next(true);

    this.userInviteService.InviteAllDependents(users, this.userType).subscribe((result: IUsersInvitationResult) => {
      const isLimitError = result.errorMessages?.some(
        (msg) => msg.toLowerCase().includes('número máximo') || msg.toLowerCase().includes('dependente'),
      );

      if (isLimitError) {
        this.notificationsService.error({
          message: TranslateService.localize('errors.max-dependents-reached'),
        });
      } else if (this.isOnlyErrorMessages(result)) {
        this.handleOnlyErrorMessages(result.errorMessages);
      } else if (!result.isSuccess) {
        this.handleInvitationFailure(result);
      } else {
        this.handleInvitationSuccess();
      }

      this.finalizeInvitationProcess();
    });
  }

  sendInvitations(): void {
    const users = this._usersList$.value;
    this._inProgress$.next(true);

    this.userInviteService.InviteAllUsers(users, this.userType).subscribe((result: IUsersInvitationResult) => {
      if (this.isOnlyErrorMessages(result)) {
        this.handleOnlyErrorMessages(result.errorMessages);
      } else if (!result.isSuccess) {
        this.handleInvitationFailure(result);
      } else {
        this.handleInvitationSuccess();
      }
      this.finalizeInvitationProcess();
    });
  }

  sendInvitationsAndSignup() {
    this.sendDependentInvitations();
    this.sendInvitations();
    this.sendSignup();
  }

  removeUserFromList(userData: TUserToInvite) {
    const usersToInvite = this._usersList$.value;
    const usersWithoutEmail = this._noEmailUsersList$.value;
    const dependentsToInvite = this._dependentsList$.value;
    this._usersList$.next(usersToInvite.filter((userDataInList) => userDataInList.email !== userData.email));
    this._noEmailUsersList$.next(usersWithoutEmail.filter((userDataInList) => userDataInList.cpf !== userData.cpf));
    this._dependentsList$.next(dependentsToInvite.filter((userDataInList) => userDataInList.cpf !== userData.cpf));
  }

  importPatient() {
    const modalComponent = this.importModalsByUserTypes[this.userType];
    this.csvImportModalRef = this.modalOverlayService.openOverlay(modalComponent);
    this.csvImportModalRef.submit$.pipe(takeUntil(this.destroy$)).subscribe((users) => this.addUsers(users));
  }

  importDoctorOrReceptionist() {
    const modalComponent = this.importModalsByUserTypes[this.userType];
    this.csvImportModalRef = this.modalOverlayService.openOverlay(modalComponent);
    this.csvImportModalRef.submit$.pipe(takeUntil(this.destroy$)).subscribe((users) => this.addUsers(users));
  }

  getModelInviteInvalids(data: TUserToInviteDTO) {
    return {
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      gender: data.gender,
      ...data,
      phone: data.phone,
      date_birth: data.date_birth ? data.date_birth : '',
    };
  }

  downloadInvalidsCsv(): void {
    this.inviteError$.subscribe((users: TUserToInviteDTO[]) => {
      const mappedUsers = users.map((data) => {
        delete data.tag;
        delete data.http_execute;
        return this.getModelInviteInvalids(data);
      });

      const csvExporter = new ExportToCsv({
        fieldSeparator: ';',
        filename: 'InvilidList',
        quoteStrings: '"',
        decimalSeparator: '.',
        showLabels: false,
        showTitle: false,
        title: '',
        useTextFile: false,
        useBom: true,
        useKeysAsHeaders: true,
        headers: [],
      });

      if (mappedUsers.length > 0) {
        csvExporter.generateCsv(mappedUsers);
      }
    });
  }

  ngOnDestroy(): void {
    this.csvImportModalRef?.close();
    this.destroy$.next();
    this._errorMessage$.next([]);
    this.invitedUsers.next(false);
  }

  get isEditMode(): boolean {
    return this.isGroup ? !!this.editableGroup : !!this.editableUser;
  }
}
